<!--
  파인애플 백오피스 — 루트 레이아웃 셸
  ─────────────────────────────────────
  · Material Dashboard 의 Sidenav/Navbar/Configurator 를
    Pineapple AdminSidebar + AdminTopbar 로 교체.
  · 내부 페이지(views/*) 는 단계별로 점진 마이그레이션 예정 — 이번 라운드는 셸만 교체.
  · 로그인/팝업 화면은 기존처럼 showSidenav / showNavbar / layout=empty 로 숨김 처리.
-->
<template>
  <notifications position="bottom right" />

  <!-- 레이아웃 없는 화면 (로그인, 팝업) -->
  <template v-if="isEmptyLayout">
    <router-view />
  </template>

  <!-- 일반 관리자 화면 — Pineapple 셸 -->
  <div v-else class="p-frame">
    <p-admin-sidebar />
    <div class="p-frame__main">
      <p-admin-topbar />
      <div class="p-frame__content">
        <router-view />
      </div>
    </div>

    <!-- 보안 동의서 미동의 강사 — 동의 전까지 시스템 전체 차단 -->
    <security-pledge-modal v-if="needPledge" />
  </div>
</template>

<script>
import PAdminSidebar from '@/components/Pineapple/AdminSidebar.vue';
import PAdminTopbar from '@/components/Pineapple/AdminTopbar.vue';
import SecurityPledgeModal from '@/components/SecurityPledgeModal.vue';

export default {
  name: 'App',
  components: {
    PAdminSidebar,
    PAdminTopbar,
    SecurityPledgeModal,
  },
  computed: {
    isEmptyLayout() {
      // 라우터 meta.layout === 'empty' 인 페이지 (로그인, 팝업, 이미지 에셋 등)
      return this.$route.meta?.layout === 'empty';
    },
    needPledge() {
      // 로그인된 "강사"가 아직 보안 동의서에 동의하지 않은 경우만 모달 표시.
      // 백엔드는 userType === 'instructor' 에게만 securityAgreement 를 내려주므로,
      // 관리자·저자 등 다른 계정에 모달이 잘못 뜨지 않도록 강사로 한정한다.
      const user = this.$store.getters['auth/getUserInfo'];
      return (
        this.$store.getters['auth/getIsLogin'] &&
        user?.userType === 'instructor' &&
        !this.$store.getters['auth/getSecurityAgreed']
      );
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/_pineapple.scss';

/* 전역 베이지 배경 + Pretendard */
html, body, #app {
  background: $p-bg;
  color: $p-ink;
  font-family: $p-font-body;
  -webkit-font-smoothing: antialiased;
  word-break: keep-all;
  min-height: 100vh;
}

.p-frame {
  width: 100%;
  min-height: 100vh;
  display: flex;
  background: $p-bg;
  color: $p-ink;
  font-family: $p-font-body;
  word-break: keep-all;
}

.p-frame__main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0; /* flex 부모에서 자식 가로 오버플로우 방지 */
}

.p-frame__content {
  flex: 1;
  overflow: auto;
  padding: 32px;
  background: $p-bg;
}
</style>

<style lang="scss">
/* ─────────────────────────────────────
   전역 비-scoped 스타일 (기존 유지)
   ───────────────────────────────────── */

.dataTable-pagination-list .active a {
  background-image: linear-gradient(195deg, #66bb6a 0%, #43a047 100%) !important;
}

/* ─────────────────────────────────────
   vue3-notification — Pineapple 톤 오버라이드
   ───────────────────────────────────── */
.vue-notification-group {
  margin: 16px;
}

.vue-notification-wrapper {
  margin-bottom: 8px;
}

.vue-notification {
  margin: 0;
  padding: 14px 16px 14px 18px;
  border-radius: 10px;
  background: #FFFFFF !important;
  color: #1F1B14 !important;
  border: 1px solid #E6DCC4;
  border-left: 4px solid #1F1B14;
  box-shadow: 0 8px 24px -8px rgba(31, 27, 20, 0.22);
  font-family: 'Pretendard', -apple-system, sans-serif;
}

.vue-notification .notification-title {
  font-size: 13px;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin-bottom: 4px;
  color: #1F1B14;
}

.vue-notification .notification-content {
  font-size: 12px;
  line-height: 1.55;
  color: #3D372D;
  margin-top: 2px;
}

/* 시맨틱 색상 */
.vue-notification.success {
  border-left-color: #1E8C66 !important;
  background: #E1F1EA !important;
  .notification-title { color: #1E8C66; }
}
.vue-notification.warn {
  border-left-color: #C98A0A !important;
  background: #FFF3C9 !important;
  .notification-title { color: #C98A0A; }
}
.vue-notification.error {
  border-left-color: #C44A3A !important;
  background: #FBE9E5 !important;
  .notification-title { color: #C44A3A; }
}
.vue-notification.info {
  border-left-color: #3B7BC4 !important;
  background: #E6F0FB !important;
  .notification-title { color: #3B7BC4; }
}

/* 모달 오버레이 스타일 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1040;
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-container {
  position: relative;
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  z-index: 1050;
  max-width: 600px;
  width: 100%;
}

.modal-close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  color: #333;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1051;
}

.modal-close-btn:hover {
  color: #000;
}

/* 문제 텍스트에서 연속된 공백 보존 */
.question-text {
  white-space: pre-wrap !important;
}

.question-text p,
.question-text div,
.question-text span {
  white-space: pre-wrap !important;
}
</style>
